import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  ArrowLeft, ArrowRight, Check, Plus, X, Camera, Video, 
  Music, User, Briefcase, Globe, Phone, Settings, Save, Upload, Loader2, Sparkles
} from "lucide-react";
import MultiSelectWithCustom from "@/components/MultiSelectWithCustom";
import AIProfileBuilder from "@/components/AIProfileBuilder";
import { ProfileEditHints } from "@/components/OnboardingHints";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

const FORM_STEPS = [
  { id: 1, title: "Personal Info", icon: User },
  { id: 2, title: "Professional", icon: Briefcase },
  { id: 3, title: "Portfolio", icon: Camera },
  { id: 4, title: "Social", icon: Globe },
  { id: 5, title: "Contact", icon: Phone },
  { id: 6, title: "Preferences", icon: Settings },
  { id: 7, title: "Visibility", icon: Settings }
];

export default function ProfileCreateEnhanced({ isEdit = false }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({});
  const [fetchingProfile, setFetchingProfile] = useState(true); // Always check for existing profile
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    personal_info: {
      full_name: "",
      display_name: "",
      gender: "",
      date_of_birth: "",
      height_cm: "",
      weight_kg: "",
      body_type: "",
      skin_tone: "",
      eye_color: "",
      hair_color: "",
      languages: [],
      city: "",
      state: "",
      country: "",
      willing_to_travel: false,
      passport_available: false
    },
    professional_info: {
      primary_category: "",
      secondary_categories: [],
      sub_categories: [],
      years_of_experience: "",
      union_association: "",
      acting_styles: [],
      dance_forms: [],
      singing_genres: [],
      voice_type: "",
      skills: [],
      past_work: [],
      awards: [],
      training: [],
      availability: ""
    },
    portfolio: {
      profile_photo: "",
      additional_photos: [],
      intro_video: "",
      showreel_videos: [],
      audio_clips: []
    },
    social_presence: {
      instagram: "",
      facebook: "",
      youtube: "",
      twitter: "",
      linkedin: "",
      imdb: "",
      personal_website: "",
      other_links: [],
      follower_count: "",
      engagement_level: ""
    },
    contact_management: {
      contact_email: "",
      contact_phone: "",
      phone_visible: false,
      managed_by: "Self",
      agency_name: "",
      agent_contact: ""
    },
    preferences: {
      preferred_work_types: [],
      preferred_locations: [],
      comfortable_print_ads: true,
      comfortable_bold_content: false,
      comfortable_travel_shoots: true,
      terms_accepted: false
    },
    visibility: {
      profile_visibility: "Public",
      allow_ratings: true,
      allow_direct_contact: true
    },
    is_draft: false
  });

  // Past work entry state
  const [newPastWork, setNewPastWork] = useState({ project_name: "", role: "", year: "", platform: "" });
  const [newAward, setNewAward] = useState("");
  const [newTraining, setNewTraining] = useState("");
  const [newSocialLink, setNewSocialLink] = useState({ platform: "", url: "" });
  
  // File upload refs and state
  const profilePhotoRef = useRef(null);
  const additionalPhotoRef = useRef(null);
  const introVideoRef = useRef(null);
  const showreelRef = useRef(null);
  const audioRef = useRef(null);
  const [uploading, setUploading] = useState({ type: null, progress: false });
  const [profileExists, setProfileExists] = useState(false); // Track if profile already exists

  useEffect(() => {
    fetchOptions();
    // ALWAYS check for existing profile (profile is auto-created during registration)
    fetchExistingProfile();
  }, []);

  const fetchOptions = async () => {
    try {
      // Fetch from taxonomy API - the single source of truth
      const response = await fetch(`${API_URL}/api/taxonomy`);
      if (response.ok) {
        const data = await response.json();
        const grouped = data.grouped || {};
        
        // Transform taxonomy data to options format + add static options
        setOptions({
          // Dynamic taxonomy options
          categories: (grouped.category || []).map(c => c.value),
          skills: (grouped.skill || []).map(s => s.value),
          cities: (grouped.city || []).map(c => c.value),
          media_categories: (grouped.media_category || []).map(m => m.value),
          
          // Static options (not in taxonomy)
          genders: ["Male", "Female", "Non-binary", "Other", "Prefer not to say"],
          body_types: ["Slim", "Athletic", "Average", "Muscular", "Curvy", "Plus Size"],
          skin_tones: ["Fair", "Wheatish", "Medium", "Olive", "Dark", "Very Dark"],
          eye_colors: ["Black", "Brown", "Hazel", "Green", "Blue", "Gray", "Amber"],
          hair_colors: ["Black", "Brown", "Blonde", "Red", "Gray", "White", "Auburn", "Colored/Dyed"],
          languages: ["Hindi", "English", "Tamil", "Telugu", "Kannada", "Malayalam", "Marathi", "Bengali", "Gujarati", "Punjabi", "Urdu", "Sanskrit", "French", "Spanish", "German"],
          indian_states: ["Maharashtra", "Karnataka", "Tamil Nadu", "Telangana", "Kerala", "West Bengal", "Gujarat", "Rajasthan", "Punjab", "Haryana", "Uttar Pradesh", "Madhya Pradesh", "Delhi", "Goa", "Andhra Pradesh"],
          countries: ["India", "USA", "UK", "Canada", "Australia", "UAE", "Singapore", "Germany", "France"],
          acting_styles: ["Method", "Classical", "Naturalistic", "Meisner", "Commercial", "Improv", "Theatre", "Film"],
          dance_forms: ["Bharatanatyam", "Kathak", "Odissi", "Kuchipudi", "Contemporary", "Hip Hop", "Jazz", "Ballet", "Bollywood", "Folk", "Salsa", "Freestyle"],
          singing_genres: ["Playback", "Classical Hindustani", "Classical Carnatic", "Ghazal", "Pop", "Rock", "Folk", "Devotional", "Western Classical"],
          platforms: ["Instagram", "YouTube", "LinkedIn", "Twitter/X", "Facebook", "TikTok", "IMDb", "Website"],
          availability_options: ["Full Time", "Part Time", "Weekends Only", "Project Based", "Flexible"],
          engagement_levels: ["High", "Medium", "Low"],
          work_types: ["Feature Films", "Short Films", "TV Series", "Web Series", "Commercials", "Music Videos", "Theatre", "Corporate Films", "Documentary"]
        });
        console.log("[OPTIONS] Taxonomy loaded:", (grouped.category || []).length, "categories,", (grouped.skill || []).length, "skills");
      } else {
        console.error("Failed to fetch taxonomy options");
      }
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const fetchExistingProfile = async () => {
    setFetchingProfile(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.profile_id) {
          // Profile exists (auto-created during registration)
          setProfileExists(true);
          setFormData({
            personal_info: { ...formData.personal_info, ...data.personal_info },
            professional_info: { ...formData.professional_info, ...data.professional_info },
            portfolio: { ...formData.portfolio, ...data.portfolio },
            social_presence: { ...formData.social_presence, ...data.social_presence },
            contact_management: { ...formData.contact_management, ...data.contact_management },
            preferences: { ...formData.preferences, ...data.preferences },
            visibility: { ...formData.visibility, ...data.visibility },
            is_draft: data.is_draft !== undefined ? data.is_draft : true
          });
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setFetchingProfile(false);
    }
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Handle AI Profile Builder output
  const handleAIApply = (aiContent) => {
    setFormData(prev => ({
      ...prev,
      professional_info: {
        ...prev.professional_info,
        bio: aiContent.bio || prev.professional_info?.bio || "",
        headline: aiContent.headline || prev.professional_info?.headline || "",
        primary_category: aiContent.category || prev.professional_info?.primary_category || "",
        skills: aiContent.skills && aiContent.skills.length > 0 
          ? [...new Set([...(prev.professional_info?.skills || []), ...aiContent.skills])]
          : prev.professional_info?.skills || []
      }
    }));
    toast.success("AI suggestions applied! Review your profile content.");
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...formData,
        is_draft: true,
        personal_info: {
          ...formData.personal_info,
          height_cm: formData.personal_info.height_cm ? parseInt(formData.personal_info.height_cm) : null,
          weight_kg: formData.personal_info.weight_kg ? parseInt(formData.personal_info.weight_kg) : null
        },
        professional_info: {
          ...formData.professional_info,
          years_of_experience: formData.professional_info.years_of_experience ? 
            parseInt(formData.professional_info.years_of_experience) : null
        },
        social_presence: {
          ...formData.social_presence,
          follower_count: formData.social_presence.follower_count ? 
            parseInt(formData.social_presence.follower_count) : null
        }
      };

      // Always use PUT - profile is auto-created during registration
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success("Draft saved!");
        setProfileExists(true);
      } else {
        const err = await response.json();
        throw new Error(err.detail || "Failed to save draft");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.personal_info.full_name || !formData.professional_info.primary_category) {
      toast.error("Please fill in required fields: Full Name and Primary Category");
      return;
    }

    if (!formData.preferences.terms_accepted) {
      toast.error("Please accept the Terms & Privacy Policy");
      setCurrentStep(6);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...formData,
        is_draft: false,
        personal_info: {
          ...formData.personal_info,
          height_cm: formData.personal_info.height_cm ? parseInt(formData.personal_info.height_cm) : null,
          weight_kg: formData.personal_info.weight_kg ? parseInt(formData.personal_info.weight_kg) : null
        },
        professional_info: {
          ...formData.professional_info,
          years_of_experience: formData.professional_info.years_of_experience ? 
            parseInt(formData.professional_info.years_of_experience) : null
        },
        social_presence: {
          ...formData.social_presence,
          follower_count: formData.social_presence.follower_count ? 
            parseInt(formData.social_presence.follower_count) : null
        }
      };

      // Always use PUT - profile is auto-created during registration
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to save profile");
      }

      toast.success("Profile saved successfully!");
      navigate("/talent/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Real file upload handler
  const handleFileUpload = async (file, fileType, field) => {
    if (!file) return;
    
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", fileType);
    
    setUploading({ type: field, progress: true });
    
    try {
      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Upload failed");
      }
      
      const data = await response.json();
      const fileUrl = `${API_URL}${data.url}`;
      
      // Update the appropriate field based on the upload type
      if (field === "profile_photo") {
        updateFormData("portfolio", "profile_photo", fileUrl);
      } else if (field === "additional_photos") {
        updateFormData("portfolio", "additional_photos", [...formData.portfolio?.additional_photos || [], fileUrl]);
      } else if (field === "intro_video") {
        updateFormData("portfolio", "intro_video", fileUrl);
      } else if (field === "showreel_videos") {
        updateFormData("portfolio", "showreel_videos", [...formData.portfolio?.showreel_videos || [], fileUrl]);
      } else if (field === "audio_clips") {
        updateFormData("portfolio", "audio_clips", [...formData.portfolio?.audio_clips || [], fileUrl]);
      }
      
      toast.success(`${fileType === 'photo' ? 'Photo' : fileType === 'video' ? 'Video' : 'Audio'} uploaded successfully!`);
    } catch (error) {
      toast.error(error.message || "Failed to upload file");
    } finally {
      setUploading({ type: null, progress: false });
    }
  };
  
  // Handle profile photo upload
  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, "photo", "profile_photo");
    }
  };
  
  // Handle additional photo upload
  const handleAdditionalPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("file_type", "photo");
      
      setUploading({ type: "additional_photos", progress: true });
      
      fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData
      })
        .then(res => res.json())
        .then(data => {
          const fileUrl = `${API_URL}${data.url}`;
          updateFormData("portfolio", "additional_photos", [...(formData.portfolio?.additional_photos || []), fileUrl]);
          toast.success("Photo uploaded!");
        })
        .catch(err => toast.error("Upload failed"))
        .finally(() => setUploading({ type: null, progress: false }));
    }
  };
  
  // Handle video upload
  const handleVideoUpload = (e, field) => {
    const file = e.target.files?.[0];
    if (file) {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("file_type", "video");
      
      setUploading({ type: field, progress: true });
      
      fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData
      })
        .then(res => res.json())
        .then(data => {
          const fileUrl = `${API_URL}${data.url}`;
          if (field === "intro_video") {
            updateFormData("portfolio", "intro_video", fileUrl);
          } else {
            updateFormData("portfolio", "showreel_videos", [...(formData.portfolio?.showreel_videos || []), fileUrl]);
          }
          toast.success("Video uploaded!");
        })
        .catch(err => toast.error("Upload failed"))
        .finally(() => setUploading({ type: null, progress: false }));
    }
  };
  
  // Handle audio upload
  const handleAudioUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("file_type", "audio");
      
      setUploading({ type: "audio_clips", progress: true });
      
      fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData
      })
        .then(res => res.json())
        .then(data => {
          const fileUrl = `${API_URL}${data.url}`;
          updateFormData("portfolio", "audio_clips", [...(formData.portfolio?.audio_clips || []), fileUrl]);
          toast.success("Audio uploaded!");
        })
        .catch(err => toast.error("Upload failed"))
        .finally(() => setUploading({ type: null, progress: false }));
    }
  };

  // Add past work
  const addPastWork = () => {
    if (newPastWork.project_name && newPastWork.role) {
      updateFormData("professional_info", "past_work", [
        ...formData.professional_info.past_work,
        { ...newPastWork, year: newPastWork.year ? parseInt(newPastWork.year) : null }
      ]);
      setNewPastWork({ project_name: "", role: "", year: "", platform: "" });
    }
  };

  // Add award
  const addAward = () => {
    if (newAward.trim()) {
      updateFormData("professional_info", "awards", [...formData.professional_info.awards, newAward.trim()]);
      setNewAward("");
    }
  };

  // Add training
  const addTraining = () => {
    if (newTraining.trim()) {
      updateFormData("professional_info", "training", [...formData.professional_info.training, newTraining.trim()]);
      setNewTraining("");
    }
  };

  // Add social link
  const addSocialLink = () => {
    if (newSocialLink.platform && newSocialLink.url) {
      updateFormData("social_presence", "other_links", [...formData.social_presence.other_links, newSocialLink]);
      setNewSocialLink({ platform: "", url: "" });
    }
  };

  const completionPercentage = Math.round(
    (currentStep / FORM_STEPS.length) * 100
  );

  if (fetchingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/talent/dashboard")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
                <span className="font-display text-xl font-bold text-gray-900">
                  {isEdit ? "Edit Profile" : "Create Profile"}
                </span>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={handleSaveDraft}
              disabled={saving}
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Draft"}
            </Button>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Step {currentStep} of {FORM_STEPS.length}</span>
            <span className="text-sm font-medium text-violet-600">{completionPercentage}% Complete</span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4 overflow-x-auto pb-2">
            {FORM_STEPS.map((step) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`flex flex-col items-center min-w-[80px] ${
                  currentStep === step.id ? 'text-violet-600' : 
                  currentStep > step.id ? 'text-teal-600' : 'text-gray-400'
                }`}
                data-testid={`step-${step.id}-btn`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                  currentStep === step.id ? 'bg-violet-100 text-violet-600' :
                  currentStep > step.id ? 'bg-teal-100 text-teal-600' : 'bg-gray-100'
                }`}>
                  {currentStep > step.id ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
                </div>
                <span className="text-xs font-medium hidden sm:block">{step.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Form */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="page-enter">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display flex items-center gap-2">
                    <User className="w-5 h-5 text-violet-600" />
                    Personal Information
                  </CardTitle>
                  <Button 
                    variant="outline"
                    onClick={() => setShowAIBuilder(true)}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="build-with-ai-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Build with AI
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Onboarding Hint for first-time users */}
                <ProfileEditHints 
                  onAIClick={() => setShowAIBuilder(true)}
                  onPhotoClick={() => setCurrentStep(3)}
                  onPublishClick={() => setCurrentStep(7)}
                />
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.personal_info.full_name}
                      onChange={(e) => updateFormData("personal_info", "full_name", e.target.value)}
                      placeholder="Your legal name"
                      className="mt-1"
                      data-testid="full-name-input"
                    />
                  </div>
                  <div>
                    <Label>Display Name / Screen Name</Label>
                    <Input
                      value={formData.personal_info.display_name}
                      onChange={(e) => updateFormData("personal_info", "display_name", e.target.value)}
                      placeholder="Stage name (optional)"
                      className="mt-1"
                      data-testid="display-name-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Gender</Label>
                    <MultiSelectWithCustom
                      options={options.genders || []}
                      selected={formData.personal_info.gender ? [formData.personal_info.gender] : []}
                      onChange={(vals) => updateFormData("personal_info", "gender", vals[0] || "")}
                      placeholder="Select gender"
                      single
                      data-testid="gender-select"
                    />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={formData.personal_info.date_of_birth}
                      onChange={(e) => updateFormData("personal_info", "date_of_birth", e.target.value)}
                      className="mt-1"
                      data-testid="dob-input"
                    />
                  </div>
                  <div>
                    <Label>Age (Auto-calculated)</Label>
                    <Input
                      value={calculateAge(formData.personal_info.date_of_birth)}
                      disabled
                      className="mt-1 bg-gray-50"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-4 gap-4">
                  <div>
                    <Label>Height (cm)</Label>
                    <Input
                      type="number"
                      value={formData.personal_info.height_cm}
                      onChange={(e) => updateFormData("personal_info", "height_cm", e.target.value)}
                      placeholder="170"
                      className="mt-1"
                      data-testid="height-input"
                    />
                  </div>
                  <div>
                    <Label>Weight (kg)</Label>
                    <Input
                      type="number"
                      value={formData.personal_info.weight_kg}
                      onChange={(e) => updateFormData("personal_info", "weight_kg", e.target.value)}
                      placeholder="65"
                      className="mt-1"
                      data-testid="weight-input"
                    />
                  </div>
                  <div>
                    <Label>Body Type</Label>
                    <MultiSelectWithCustom
                      options={options.body_types || []}
                      selected={formData.personal_info.body_type ? [formData.personal_info.body_type] : []}
                      onChange={(vals) => updateFormData("personal_info", "body_type", vals[0] || "")}
                      placeholder="Select"
                      single
                    />
                  </div>
                  <div>
                    <Label>Skin Tone</Label>
                    <MultiSelectWithCustom
                      options={options.skin_tones || []}
                      selected={formData.personal_info.skin_tone ? [formData.personal_info.skin_tone] : []}
                      onChange={(vals) => updateFormData("personal_info", "skin_tone", vals[0] || "")}
                      placeholder="Select"
                      single
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Eye Color</Label>
                    <MultiSelectWithCustom
                      options={options.eye_colors || []}
                      selected={formData.personal_info.eye_color ? [formData.personal_info.eye_color] : []}
                      onChange={(vals) => updateFormData("personal_info", "eye_color", vals[0] || "")}
                      placeholder="Select"
                      single
                    />
                  </div>
                  <div>
                    <Label>Hair Color</Label>
                    <MultiSelectWithCustom
                      options={options.hair_colors || []}
                      selected={formData.personal_info.hair_color ? [formData.personal_info.hair_color] : []}
                      onChange={(vals) => updateFormData("personal_info", "hair_color", vals[0] || "")}
                      placeholder="Select"
                      single
                    />
                  </div>
                </div>

                <div>
                  <Label>Languages Known</Label>
                  <MultiSelectWithCustom
                    options={options.languages || []}
                    selected={formData.personal_info.languages}
                    onChange={(vals) => updateFormData("personal_info", "languages", vals)}
                    placeholder="Select languages"
                    data-testid="languages-select"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.personal_info.city}
                      onChange={(e) => updateFormData("personal_info", "city", e.target.value)}
                      placeholder="Mumbai"
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <MultiSelectWithCustom
                      options={options.indian_states || []}
                      selected={formData.personal_info.state ? [formData.personal_info.state] : []}
                      onChange={(vals) => updateFormData("personal_info", "state", vals[0] || "")}
                      placeholder="Select"
                      single
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <MultiSelectWithCustom
                      options={options.countries || []}
                      selected={formData.personal_info.country ? [formData.personal_info.country] : []}
                      onChange={(vals) => updateFormData("personal_info", "country", vals[0] || "")}
                      placeholder="Select"
                      single
                    />
                  </div>
                </div>

                <div className="flex gap-8">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={formData.personal_info.willing_to_travel}
                      onCheckedChange={(v) => updateFormData("personal_info", "willing_to_travel", v)}
                      data-testid="travel-switch"
                    />
                    <Label>Willing to Travel</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={formData.personal_info.passport_available}
                      onCheckedChange={(v) => updateFormData("personal_info", "passport_available", v)}
                      data-testid="passport-switch"
                    />
                    <Label>Passport Available</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Professional Information */}
          {currentStep === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-violet-600" />
                  Professional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Category *</Label>
                    <MultiSelectWithCustom
                      options={options.categories || []}
                      selected={formData.professional_info.primary_category ? [formData.professional_info.primary_category] : []}
                      onChange={(vals) => updateFormData("professional_info", "primary_category", vals[0] || "")}
                      placeholder="Select primary category"
                      single
                      data-testid="primary-category-select"
                    />
                  </div>
                  <div>
                    <Label>Years of Experience</Label>
                    <Input
                      type="number"
                      value={formData.professional_info.years_of_experience}
                      onChange={(e) => updateFormData("professional_info", "years_of_experience", e.target.value)}
                      placeholder="5"
                      className="mt-1"
                      data-testid="experience-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Secondary Categories</Label>
                  <MultiSelectWithCustom
                    options={options.categories || []}
                    selected={formData.professional_info.secondary_categories}
                    onChange={(vals) => updateFormData("professional_info", "secondary_categories", vals)}
                    placeholder="Select secondary categories"
                  />
                </div>

                <div>
                  <Label>Sub-Categories / Specializations</Label>
                  <MultiSelectWithCustom
                    options={formData.professional_info.primary_category && options.sub_categories ? 
                      (options.sub_categories[formData.professional_info.primary_category] || []) : []}
                    selected={formData.professional_info.sub_categories}
                    onChange={(vals) => updateFormData("professional_info", "sub_categories", vals)}
                    placeholder="Select specializations"
                  />
                </div>

                <div>
                  <Label>Union / Association</Label>
                  <Input
                    value={formData.professional_info.union_association}
                    onChange={(e) => updateFormData("professional_info", "union_association", e.target.value)}
                    placeholder="e.g., CINTAA, SAG-AFTRA"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Acting Styles / Performance Styles</Label>
                  <MultiSelectWithCustom
                    options={options.acting_styles || []}
                    selected={formData.professional_info.acting_styles}
                    onChange={(vals) => updateFormData("professional_info", "acting_styles", vals)}
                    placeholder="Select styles"
                  />
                </div>

                <div>
                  <Label>Dance Forms</Label>
                  <MultiSelectWithCustom
                    options={options.dance_forms || []}
                    selected={formData.professional_info.dance_forms}
                    onChange={(vals) => updateFormData("professional_info", "dance_forms", vals)}
                    placeholder="Select dance forms"
                  />
                </div>

                <div>
                  <Label>Singing Genres</Label>
                  <MultiSelectWithCustom
                    options={options.singing_genres || []}
                    selected={formData.professional_info.singing_genres}
                    onChange={(vals) => updateFormData("professional_info", "singing_genres", vals)}
                    placeholder="Select genres"
                  />
                </div>

                <div>
                  <Label>Voice Type / Range</Label>
                  <Input
                    value={formData.professional_info.voice_type}
                    onChange={(e) => updateFormData("professional_info", "voice_type", e.target.value)}
                    placeholder="e.g., Tenor, Soprano, Baritone"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Skills & Special Abilities</Label>
                  <MultiSelectWithCustom
                    options={options.skills || []}
                    selected={formData.professional_info.skills}
                    onChange={(vals) => updateFormData("professional_info", "skills", vals)}
                    placeholder="Select skills"
                    data-testid="skills-select"
                  />
                </div>

                {/* Past Work / Filmography */}
                <div>
                  <Label className="mb-3 block">Past Work / Filmography</Label>
                  <div className="space-y-3 mb-4">
                    {formData.professional_info.past_work.map((work, i) => (
                      <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <span className="font-medium">{work.project_name}</span>
                          <span className="text-gray-500"> - {work.role}</span>
                          {work.year && <span className="text-gray-400"> ({work.year})</span>}
                          {work.platform && <Badge variant="secondary" className="ml-2">{work.platform}</Badge>}
                        </div>
                        <button
                          onClick={() => updateFormData("professional_info", "past_work", 
                            formData.professional_info.past_work.filter((_, idx) => idx !== i)
                          )}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Input
                      value={newPastWork.project_name}
                      onChange={(e) => setNewPastWork({ ...newPastWork, project_name: e.target.value })}
                      placeholder="Project Name"
                    />
                    <Input
                      value={newPastWork.role}
                      onChange={(e) => setNewPastWork({ ...newPastWork, role: e.target.value })}
                      placeholder="Role"
                    />
                    <Input
                      type="number"
                      value={newPastWork.year}
                      onChange={(e) => setNewPastWork({ ...newPastWork, year: e.target.value })}
                      placeholder="Year"
                    />
                    <div className="flex gap-2">
                      <select
                        value={newPastWork.platform}
                        onChange={(e) => setNewPastWork({ ...newPastWork, platform: e.target.value })}
                        className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm"
                      >
                        <option value="">Platform</option>
                        {(options.platforms || []).map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <Button type="button" onClick={addPastWork} size="icon">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Awards */}
                <div>
                  <Label>Awards / Achievements</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.professional_info.awards.map((award, i) => (
                      <Badge key={i} variant="secondary" className="px-3 py-1">
                        {award}
                        <button onClick={() => updateFormData("professional_info", "awards",
                          formData.professional_info.awards.filter((_, idx) => idx !== i)
                        )} className="ml-2"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newAward}
                      onChange={(e) => setNewAward(e.target.value)}
                      placeholder="Add award or achievement"
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addAward())}
                    />
                    <Button type="button" onClick={addAward} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Training */}
                <div>
                  <Label>Training / Workshops</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.professional_info.training.map((t, i) => (
                      <Badge key={i} variant="secondary" className="px-3 py-1">
                        {t}
                        <button onClick={() => updateFormData("professional_info", "training",
                          formData.professional_info.training.filter((_, idx) => idx !== i)
                        )} className="ml-2"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newTraining}
                      onChange={(e) => setNewTraining(e.target.value)}
                      placeholder="Add training or workshop"
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTraining())}
                    />
                    <Button type="button" onClick={addTraining} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Current Availability</Label>
                  <MultiSelectWithCustom
                    options={options.availability_options || []}
                    selected={formData.professional_info.availability ? [formData.professional_info.availability] : []}
                    onChange={(vals) => updateFormData("professional_info", "availability", vals[0] || "")}
                    placeholder="Select availability"
                    single
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Portfolio & Media */}
          {currentStep === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Camera className="w-5 h-5 text-violet-600" />
                  Portfolio & Media
                </CardTitle>
                <p className="text-sm text-gray-500">Upload your professional photos, videos, and audio clips</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Hidden file inputs */}
                <input 
                  type="file" 
                  ref={profilePhotoRef} 
                  className="hidden" 
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleProfilePhotoChange}
                />
                <input 
                  type="file" 
                  ref={additionalPhotoRef} 
                  className="hidden" 
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAdditionalPhotoChange}
                />
                <input 
                  type="file" 
                  ref={introVideoRef} 
                  className="hidden" 
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={(e) => handleVideoUpload(e, "intro_video")}
                />
                <input 
                  type="file" 
                  ref={showreelRef} 
                  className="hidden" 
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={(e) => handleVideoUpload(e, "showreel_videos")}
                />
                <input 
                  type="file" 
                  ref={audioRef} 
                  className="hidden" 
                  accept="audio/mpeg,audio/wav,audio/ogg"
                  onChange={handleAudioUpload}
                />

                {/* Profile Photo */}
                <div>
                  <Label>Profile Photo</Label>
                  <p className="text-xs text-gray-500 mb-2">Upload a clear, professional headshot (JPEG, PNG, WebP, max 50MB). Portrait orientation (3:4 or 4:5) recommended.</p>
                  <div className="mt-2 flex items-start gap-4">
                    {formData.portfolio.profile_photo ? (
                      <div className="relative">
                        <div className="talent-thumb-container w-32 h-40 rounded-xl overflow-hidden">
                          <img src={formData.portfolio.profile_photo} alt="Profile" />
                        </div>
                        <button
                          onClick={() => updateFormData("portfolio", "profile_photo", "")}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => profilePhotoRef.current?.click()}
                        disabled={uploading.type === "profile_photo"}
                        className="w-32 h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:border-violet-500 hover:text-violet-500 disabled:opacity-50"
                        data-testid="upload-profile-photo-btn"
                      >
                        {uploading.type === "profile_photo" ? (
                          <Loader2 className="w-8 h-8 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mb-2" />
                            <span className="text-xs">Upload Photo</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Additional Photos */}
                <div>
                  <Label>Additional Photos</Label>
                  <p className="text-xs text-gray-500 mb-2">Add more photos to showcase your portfolio</p>
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {formData.portfolio.additional_photos.map((photo, i) => (
                      <div key={i} className="relative">
                        <div className="talent-thumb-container w-full aspect-[3/4] rounded-lg overflow-hidden">
                          <img src={photo} alt={`Photo ${i + 1}`} />
                        </div>
                        <button
                          onClick={() => updateFormData("portfolio", "additional_photos",
                            formData.portfolio.additional_photos.filter((_, idx) => idx !== i)
                          )}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => additionalPhotoRef.current?.click()}
                      disabled={uploading.type === "additional_photos"}
                      className="h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-violet-500 hover:text-violet-500 disabled:opacity-50"
                      data-testid="upload-additional-photo-btn"
                    >
                      {uploading.type === "additional_photos" ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <Plus className="w-6 h-6" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Introduction Video */}
                <div>
                  <Label>Introduction Video</Label>
                  <p className="text-xs text-gray-500 mb-2">Upload a short intro video (MP4, WebM, max 50MB)</p>
                  {formData.portfolio.intro_video ? (
                    <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Video className="w-5 h-5 text-violet-600" />
                      <span className="flex-1 text-sm truncate">Introduction Video</span>
                      <button
                        onClick={() => updateFormData("portfolio", "intro_video", "")}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="mt-2"
                      onClick={() => introVideoRef.current?.click()}
                      disabled={uploading.type === "intro_video"}
                      data-testid="upload-intro-video-btn"
                    >
                      {uploading.type === "intro_video" ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Introduction Video
                    </Button>
                  )}
                </div>

                {/* Showreel Videos */}
                <div>
                  <Label>Showreel / Performance Videos</Label>
                  <p className="text-xs text-gray-500 mb-2">Add your best performance clips</p>
                  <div className="mt-2 space-y-2">
                    {formData.portfolio.showreel_videos.map((video, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Video className="w-5 h-5 text-violet-600" />
                        <span className="flex-1 text-sm">Showreel {i + 1}</span>
                        <button
                          onClick={() => updateFormData("portfolio", "showreel_videos",
                            formData.portfolio.showreel_videos.filter((_, idx) => idx !== i)
                          )}
                          className="text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <Button 
                      variant="outline" 
                      onClick={() => showreelRef.current?.click()}
                      disabled={uploading.type === "showreel_videos"}
                    >
                      {uploading.type === "showreel_videos" ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Add Showreel
                    </Button>
                  </div>
                </div>

                {/* Audio Clips */}
                <div>
                  <Label>Audio Clips (for singers / voice artists)</Label>
                  <p className="text-xs text-gray-500 mb-2">Upload your voice samples or songs (MP3, WAV, max 50MB)</p>
                  <div className="mt-2 space-y-2">
                    {formData.portfolio.audio_clips.map((audio, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Music className="w-5 h-5 text-pink-600" />
                        <span className="flex-1 text-sm">Audio Clip {i + 1}</span>
                        <button
                          onClick={() => updateFormData("portfolio", "audio_clips",
                            formData.portfolio.audio_clips.filter((_, idx) => idx !== i)
                          )}
                          className="text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <Button 
                      variant="outline" 
                      onClick={() => audioRef.current?.click()}
                      disabled={uploading.type === "audio_clips"}
                    >
                      {uploading.type === "audio_clips" ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Add Audio Clip
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Social & Online Presence */}
          {currentStep === 4 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Globe className="w-5 h-5 text-violet-600" />
                  Social & Online Presence
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Instagram</Label>
                    <Input
                      value={formData.social_presence.instagram}
                      onChange={(e) => updateFormData("social_presence", "instagram", e.target.value)}
                      placeholder="https://instagram.com/username"
                      className="mt-1"
                      data-testid="instagram-input"
                    />
                  </div>
                  <div>
                    <Label>Facebook</Label>
                    <Input
                      value={formData.social_presence.facebook}
                      onChange={(e) => updateFormData("social_presence", "facebook", e.target.value)}
                      placeholder="https://facebook.com/username"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>YouTube Channel</Label>
                    <Input
                      value={formData.social_presence.youtube}
                      onChange={(e) => updateFormData("social_presence", "youtube", e.target.value)}
                      placeholder="https://youtube.com/@channel"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>X (Twitter)</Label>
                    <Input
                      value={formData.social_presence.twitter}
                      onChange={(e) => updateFormData("social_presence", "twitter", e.target.value)}
                      placeholder="https://x.com/username"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>LinkedIn</Label>
                    <Input
                      value={formData.social_presence.linkedin}
                      onChange={(e) => updateFormData("social_presence", "linkedin", e.target.value)}
                      placeholder="https://linkedin.com/in/username"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>IMDb</Label>
                    <Input
                      value={formData.social_presence.imdb}
                      onChange={(e) => updateFormData("social_presence", "imdb", e.target.value)}
                      placeholder="https://imdb.com/name/..."
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Personal Website / Portfolio</Label>
                  <Input
                    value={formData.social_presence.personal_website}
                    onChange={(e) => updateFormData("social_presence", "personal_website", e.target.value)}
                    placeholder="https://yourwebsite.com"
                    className="mt-1"
                  />
                </div>

                {/* Other Links */}
                <div>
                  <Label>Other Social / Platform Links</Label>
                  <div className="space-y-2 mt-2">
                    {formData.social_presence.other_links.map((link, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <Badge variant="secondary">{link.platform}</Badge>
                        <span className="flex-1 text-sm truncate">{link.url}</span>
                        <button
                          onClick={() => updateFormData("social_presence", "other_links",
                            formData.social_presence.other_links.filter((_, idx) => idx !== i)
                          )}
                          className="text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={newSocialLink.platform}
                        onChange={(e) => setNewSocialLink({ ...newSocialLink, platform: e.target.value })}
                        placeholder="Platform name"
                        className="w-1/3"
                      />
                      <Input
                        value={newSocialLink.url}
                        onChange={(e) => setNewSocialLink({ ...newSocialLink, url: e.target.value })}
                        placeholder="URL"
                        className="flex-1"
                      />
                      <Button type="button" onClick={addSocialLink} variant="outline">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Follower Count (approx)</Label>
                    <Input
                      type="number"
                      value={formData.social_presence.follower_count}
                      onChange={(e) => updateFormData("social_presence", "follower_count", e.target.value)}
                      placeholder="10000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Engagement Level</Label>
                    <MultiSelectWithCustom
                      options={options.engagement_levels || []}
                      selected={formData.social_presence.engagement_level ? [formData.social_presence.engagement_level] : []}
                      onChange={(vals) => updateFormData("social_presence", "engagement_level", vals[0] || "")}
                      placeholder="Select level"
                      single
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Contact & Management */}
          {currentStep === 5 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Phone className="w-5 h-5 text-violet-600" />
                  Contact & Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_management.contact_email}
                      onChange={(e) => updateFormData("contact_management", "contact_email", e.target.value)}
                      placeholder="your@email.com"
                      className="mt-1"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Label>Contact Phone</Label>
                    <Input
                      value={formData.contact_management.contact_phone}
                      onChange={(e) => updateFormData("contact_management", "contact_phone", e.target.value)}
                      placeholder="+91 9876543210"
                      className="mt-1"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.contact_management.phone_visible}
                    onCheckedChange={(v) => updateFormData("contact_management", "phone_visible", v)}
                  />
                  <Label>Show phone number on public profile</Label>
                </div>

                <div>
                  <Label>Managed By</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.contact_management.managed_by === "Self"}
                        onChange={() => updateFormData("contact_management", "managed_by", "Self")}
                        className="w-4 h-4 text-violet-600"
                      />
                      <span>Self</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.contact_management.managed_by === "Agency"}
                        onChange={() => updateFormData("contact_management", "managed_by", "Agency")}
                        className="w-4 h-4 text-violet-600"
                      />
                      <span>Agency</span>
                    </label>
                  </div>
                </div>

                {formData.contact_management.managed_by === "Agency" && (
                  <div className="grid sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label>Agency / Manager Name</Label>
                      <Input
                        value={formData.contact_management.agency_name}
                        onChange={(e) => updateFormData("contact_management", "agency_name", e.target.value)}
                        placeholder="Agency name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Agent / Manager Contact</Label>
                      <Input
                        value={formData.contact_management.agent_contact}
                        onChange={(e) => updateFormData("contact_management", "agent_contact", e.target.value)}
                        placeholder="Email or phone"
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 6: Preferences & Consent */}
          {currentStep === 6 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Settings className="w-5 h-5 text-violet-600" />
                  Preferences & Consent
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Preferred Work Types</Label>
                  <MultiSelectWithCustom
                    options={options.work_types || []}
                    selected={formData.preferences.preferred_work_types}
                    onChange={(vals) => updateFormData("preferences", "preferred_work_types", vals)}
                    placeholder="Select work types"
                  />
                </div>

                <div>
                  <Label>Preferred Work Locations</Label>
                  <MultiSelectWithCustom
                    options={["Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "International"]}
                    selected={formData.preferences.preferred_locations}
                    onChange={(vals) => updateFormData("preferences", "preferred_locations", vals)}
                    placeholder="Select locations"
                  />
                </div>

                <div className="space-y-4">
                  <Label className="text-base font-semibold">Comfortable With</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span>Print Advertisements</span>
                      <Switch
                        checked={formData.preferences.comfortable_print_ads}
                        onCheckedChange={(v) => updateFormData("preferences", "comfortable_print_ads", v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span>Bold / Intimate Content</span>
                      <Switch
                        checked={formData.preferences.comfortable_bold_content}
                        onCheckedChange={(v) => updateFormData("preferences", "comfortable_bold_content", v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span>Travel for Shoots</span>
                      <Switch
                        checked={formData.preferences.comfortable_travel_shoots}
                        onCheckedChange={(v) => updateFormData("preferences", "comfortable_travel_shoots", v)}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.preferences.terms_accepted}
                      onChange={(e) => updateFormData("preferences", "terms_accepted", e.target.checked)}
                      className="mt-1 w-5 h-5 text-violet-600 rounded"
                      data-testid="terms-checkbox"
                    />
                    <div>
                      <Label className="cursor-pointer">
                        I accept the Terms of Service and Privacy Policy *
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        By checking this box, you agree to our terms and conditions and consent to the processing of your data.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 7: Visibility & Settings */}
          {currentStep === 7 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Settings className="w-5 h-5 text-violet-600" />
                  Visibility & Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-base font-semibold">Profile Visibility</Label>
                  <div className="flex gap-4 mt-3">
                    <label className={`flex-1 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.visibility.profile_visibility === "Public" ? 'border-violet-500 bg-violet-50' : 'border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        checked={formData.visibility.profile_visibility === "Public"}
                        onChange={() => updateFormData("visibility", "profile_visibility", "Public")}
                        className="sr-only"
                      />
                      <div className="font-semibold text-gray-900">Public</div>
                      <p className="text-sm text-gray-500">Anyone can view your profile</p>
                    </label>
                    <label className={`flex-1 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.visibility.profile_visibility === "Private" ? 'border-violet-500 bg-violet-50' : 'border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        checked={formData.visibility.profile_visibility === "Private"}
                        onChange={() => updateFormData("visibility", "profile_visibility", "Private")}
                        className="sr-only"
                      />
                      <div className="font-semibold text-gray-900">Private</div>
                      <p className="text-sm text-gray-500">Only approved users can view</p>
                    </label>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">Allow Ratings</div>
                      <p className="text-sm text-gray-500">Let others rate your profile</p>
                    </div>
                    <Switch
                      checked={formData.visibility.allow_ratings}
                      onCheckedChange={(v) => updateFormData("visibility", "allow_ratings", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">Allow Direct Contact</div>
                      <p className="text-sm text-gray-500">Let casting directors contact you directly</p>
                    </div>
                    <Switch
                      checked={formData.visibility.allow_direct_contact}
                      onCheckedChange={(v) => updateFormData("visibility", "allow_direct_contact", v)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            
            {currentStep < FORM_STEPS.length ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="btn-primary"
                data-testid="next-step-btn"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                className="btn-primary"
                disabled={loading}
                data-testid="submit-profile-btn"
              >
                {loading ? "Saving..." : (isEdit ? "Update Profile" : "Create Profile")}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* AI Profile Builder Modal */}
      <AIProfileBuilder
        isOpen={showAIBuilder}
        onClose={() => setShowAIBuilder(false)}
        profileType="talent"
        onApply={handleAIApply}
        existingData={{
          bio: formData.professional_info?.bio,
          headline: formData.professional_info?.headline,
          skills: formData.professional_info?.skills
        }}
      />
    </div>
  );
}
